import React, { useEffect } from "react";
import "./speakerModal.css";

interface SpeakerModalProps {
  isOpen: boolean;
  onClose: () => void;
  topic?: string;
  speaker: {
    name: string;
    role?: string; // e.g. "Founder and CEO of ..."
    headline?: string; // optional short highlight (e.g. "#1 NYT Best-Selling Author")
    fullBio?: string;
    imageUrl?: string;
    tags?: string[]; // optional badges, e.g. ["Author", "Celebrity"]
    profileUrl?: string; // optional external profile link
  } | null;
}

const placeholderImage =
  "https://res.cloudinary.com/astcdubai/image/upload/v1736906559/placeholder_male.jpg";

const SpeakerModal: React.FC<SpeakerModalProps> = ({
  isOpen,
  onClose,
  speaker,
  topic,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  if (!isOpen || !speaker) return null;

  const imgSrc = speaker.imageUrl || placeholderImage;
  // Custom role override for specific speakers
  let displayRole = speaker.role;
  if (speaker.name?.toLowerCase().includes("sony kapoor")) {
    displayRole =
      "An influential macroeconomist, geostrategist, and investment expert who advises several G-20 and EU governments, large institutional investors, international organizations, and multinational companies";
  }

  // Optional: if you want to show a different tag or replace all tags
  const tags = speaker.tags ?? (displayRole ? [displayRole] : []);

  return (
    <div
      className="sv-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sv-title"
    >
      <div className="sv-sheet" onClick={(e) => e.stopPropagation()}>
        <button className="sv-close" aria-label="Close" onClick={onClose}>
          ×
        </button>

        <div className="sv-left">
          <img
            src={imgSrc}
            alt={speaker.name}
            className="sv-photo"
            loading="lazy"
          />
        </div>

        <div className="sv-right">
          <h2 id="sv-title" className="sv-name">
            {speaker.name}
          </h2>
          {speaker.headline && (
            <div className="sv-headline">{speaker.headline}</div>
          )}

          {tags.length > 0 && (
            <div className="sv-tags">
              {tags.map((t, i) => (
                <span className="sv-tag" key={i}>
                  {t}
                </span>
              ))}
            </div>
          )}

          {speaker.fullBio && (
            <div className="sv-bio-scroll">
              <p className="sv-bio">{speaker.fullBio}</p>
            </div>
          )}

          {topic && (
            <div className="sv-topic">
              🎤 Listen to {speaker.name} on:
              <span className="sv-topic-text"> {topic}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpeakerModal;
